import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  Stack,
  Button,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MapIcon from "@mui/icons-material/Map";
import CompareIcon from "@mui/icons-material/Compare";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const formatCurrency = (value, compact = false) => {
  if (compact) {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    }
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const NetworkComparisonDashboard = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([
    "Palo Alto",
    "Cisco",
    "Aruba",
    "Juniper",
  ]);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const handleVendorChange = (event, newVendors) => {
    if (newVendors.length) {
      setSelectedVendors(newVendors);
    }
  };

  const totalCosts = [
    {
      vendor: "Palo Alto",
      cost: 1622140,
      formattedCost: formatCurrency(1622140),
    },
    { vendor: "Cisco", cost: 5716910, formattedCost: formatCurrency(5716910) },
    { vendor: "Aruba", cost: 2980702, formattedCost: formatCurrency(2980702) },
    {
      vendor: "Juniper",
      cost: 7456481,
      formattedCost: formatCurrency(7456481),
    },
  ];

  const costPerSiteData = [
    {
      category: "Small Sites",
      "Palo Alto": 10070,
      Cisco: 9916,
      Aruba: 10598,
      Juniper: 15142,
      formattedPaloAlto: formatCurrency(10070),
      formattedCisco: formatCurrency(9916),
      formattedAruba: formatCurrency(10598),
      formattedJuniper: formatCurrency(15142),
    },
    {
      category: "Medium Sites",
      "Palo Alto": 12080,
      Cisco: 9916,
      Aruba: 26518,
      Juniper: 75857,
      formattedPaloAlto: formatCurrency(12080),
      formattedCisco: formatCurrency(9916),
      formattedAruba: formatCurrency(26518),
      formattedJuniper: formatCurrency(75857),
    },
    {
      category: "Large Sites",
      "Palo Alto": 21800,
      Cisco: 40898,
      Aruba: 40686,
      Juniper: 145716,
      formattedPaloAlto: formatCurrency(21800),
      formattedCisco: formatCurrency(40898),
      formattedAruba: formatCurrency(40686),
      formattedJuniper: formatCurrency(145716),
    },
    {
      category: "DC Sites",
      "Palo Alto": 32100,
      Cisco: 572812,
      Aruba: 107014,
      Juniper: 225920,
      formattedPaloAlto: formatCurrency(32100),
      formattedCisco: formatCurrency(572812),
      formattedAruba: formatCurrency(107014),
      formattedJuniper: formatCurrency(225920),
    },
  ];

  const vendorAnalysis = {
    "Palo Alto": {
      strengths: [
        "Most aligned to Client Requirements",
        "Only solution with true Layer 7 control and granularity",
        "Strategic Integration with future state of Palo Alto ecosystem",
        "Most cost effective pricing model",
        "Hardware availability and lead times",
        "Superior cloud integration",
        "ZTP deployment speed",
        "Most security features and capabilities",
      ],
      weaknesses: [
        "Limited hardware options",
        "BGP and OSPF routing only",
        "Higher learning curve",
      ],
    },
    Cisco: {
      strengths: [
        "Support for additional routing protocols (EIGRP and RIP)",
        "Strong infrastructure integration",
        "Robust Hardware",
        "Integration",
      ],
      weaknesses: [
        "Hardware availability",
        "High Total Cost",
        "Additional HA license pricing",
        "Management Complexity",
      ],
    },
    Aruba: {
      strengths: [
        "WAN optimization capability",
        "Competitive pricing",
        "Integration with existing products",
        "Unified management",
        "Hardware availability and lead times",
      ],
      weaknesses: [
        "BGP and OSPF routing only",
        "Hardware limitations to 10GB",
        "Path selection lacks standout features beyond metrics.",
        "Additional HA license pricing",
        "Less mature cloud integration",
      ],
    },
    Juniper: {
      strengths: [
        "Most AI leveraged model",
        "Hardware with highest memory capacity",
        "Second best in requirements alignment",
        "Unique tunnel utilization technology",
        "Quick setup and deployment",
      ],
      weaknesses: [
        "Full hardware suite needed for user visibility",
        "BGP and OSPF routing only",
        "Hardware cost and availability",
        "Additional HA license pricing",
      ],
    },
  };

  const COLORS = {
    "Palo Alto": "#8884d8",
    Cisco: "#82ca9d",
    Aruba: "#ffc658",
    Juniper: "#ff7300",
  };

  const MenuDrawer = () => (
    <Drawer anchor="right" open={menuOpen} onClose={handleMenuToggle}>
      <Box sx={{ width: 250, p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle2" color="text.secondary">
            Navigation
          </Typography>
          <Button
            variant="outlined"
            component={Link}
            to="/"
            startIcon={<MapIcon />}
          >
            Location Map
          </Button>
          <Button
            variant="outlined"
            component={Link}
            to="/network-comparison"
            startIcon={<CompareIcon />}
          >
            Network Hardware
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1 }}>
          <Typography variant="body2">{label}</Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" color={entry.color}>
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="static"
        sx={{ backgroundColor: "white", color: "black" }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">Network Comparison Dashboard</Typography>
          </Box>
          <IconButton color="inherit" onClick={handleMenuToggle}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, flexGrow: 1 }}>
        <Stack spacing={3}>
          {/* Controls */}
          <Paper elevation={3}>
            <Box sx={{ p: 2 }}>
              <ToggleButtonGroup
                value={selectedVendors}
                onChange={handleVendorChange}
                aria-label="vendor selection"
                sx={{ flexWrap: "wrap" }}
              >
                {Object.keys(COLORS).map((vendor) => (
                  <ToggleButton key={vendor} value={vendor}>
                    {vendor}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Paper>

          {/* Cost Charts */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Paper elevation={3} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Total Cost Comparison
                </Typography>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={totalCosts.filter((item) =>
                        selectedVendors.includes(item.vendor)
                      )}
                    >
                      <XAxis dataKey="vendor" />
                      <YAxis
                        tickFormatter={(value) => formatCurrency(value, true)}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="cost" fill="#8884d8" name="Total Cost" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Paper>

            <Paper elevation={3} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Cost per Site Type
                </Typography>
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={costPerSiteData}>
                      <XAxis dataKey="category" />
                      <YAxis
                        tickFormatter={(value) => formatCurrency(value, true)}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {selectedVendors.map((vendor) => (
                        <Bar
                          key={vendor}
                          dataKey={vendor}
                          fill={COLORS[vendor]}
                          name={vendor}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Strengths/Weaknesses */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {selectedVendors.map((vendor) => (
              <Paper
                elevation={3}
                key={vendor}
                sx={{ flex: { xs: "1 1 100%", md: "1 1 calc(50% - 8px)" } }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {vendor}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: "center",
                          mb: 2,
                          color: "#4caf50",
                          fontWeight: "bold",
                        }}
                      >
                        PRO
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color="white"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                          bgcolor: "#4caf50",
                          p: 1,
                          borderRadius: "4px 4px 0 0",
                        }}
                      >
                        <CheckCircleIcon /> Strengths
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: "#4caf50",
                          p: 2,
                          borderRadius: "0 0 4px 4px",
                          minHeight: 200,
                          "& > *": { color: "white" },
                        }}
                      >
                        {vendorAnalysis[vendor].strengths.map(
                          (strength, index) => (
                            <Typography key={index} sx={{ mb: 1 }}>
                              • {strength}
                            </Typography>
                          )
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: "center",
                          mb: 2,
                          color: "#2196f3",
                          fontWeight: "bold",
                        }}
                      >
                        CON
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color="white"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                          bgcolor: "#2196f3",
                          p: 1,
                          borderRadius: "4px 4px 0 0",
                        }}
                      >
                        <CancelIcon /> Weaknesses
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: "#2196f3",
                          p: 2,
                          borderRadius: "0 0 4px 4px",
                          minHeight: 200,
                          "& > *": { color: "white" },
                        }}
                      >
                        {vendorAnalysis[vendor].weaknesses.map(
                          (weakness, index) => (
                            <Typography key={index} sx={{ mb: 1 }}>
                              • {weakness}
                            </Typography>
                          )
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Stack>
      </Box>

      <MenuDrawer />
    </Box>
  );
};

export default NetworkComparisonDashboard;